import Tooltip from './components/Tooltip.vue';
import VideoConsent from './components/VideoConsent.vue';
import ApiSourceSet from './components/ApiSourceSet.vue';

import { readQuestionnaire, createResponse, readResponse, updateResponse, finishResponse } from './../services/apiService.js';

import constants from './../constants.js';

import 'sweetalert2/src/sweetalert2.scss';

const questionnaireEmbedApp = {
    components: {
        Tooltip,
        VideoConsent,
        ApiSourceSet,
    },
    data() {
        return {
            constants: constants,
            loading: false,
            questionnaire: null,
            showModal: false,
            page: 0,
            answers: {},
            response: null,
            isProgressing: true,
            fullResponse: null,

            dataGuid: '',
            dataCulture: 'nl-NL',
            dataTheme: '',
            dataHideCookie: false,
            dataQuestionXofYLabel: 'Vraag {current} van {total}',
            dataSelectedXofYLabel: '{selected} van {total} geselecteerd',
            dataStopQuestionnaireTitle: 'Weet je zeker dat je wilt stoppen?',
            dataStopQuestionnaireText: 'Alle ingevulde antwoorden zullen hierbij verloren gaan.',
            dataStopQuestionnaireConfirm: 'Ja, ik wil stoppen',
            dataStopQuestionnaireCancel: 'Hervat de test',

            dataExplanationsJson: [],
            dataKnowledgeBlocksJson: [],

            dataReferAfterCancel: false,
            dataReferralPage: '',

            isIframe: false,
        };
    },
    computed: {
        modalTitle() {
            switch (this.page) {
                case 0:
                    return !this.isIframe ? this.questionnaire?.title : '';
                case this.questionnaire?.questions?.length + 1:
                    return this.questionnaire?.thankyouText;
                default:
                    return `${this.page}. ${this.questionnaire?.questions?.find(q => q.position === this.page)?.text}`;
            }
        },
        activeQuestion() {
            return this.questionnaire?.questions?.find(q => q.position === this.page);
        },
        activeExplanations() {
            if (!this.activeQuestion) return [];
            return this.dataExplanationsJson.filter(e => e.questionId === this.activeQuestion.id);
        },
        activeKnowledgeBlocks() {
            if (!this.activeQuestion) return [];
            const selected = this.answers[this.activeQuestion.id];
            const selectedArray = Array.isArray(selected) ? selected : selected ? [selected] : [];

            return this.dataKnowledgeBlocksJson.filter(kb =>
                kb.questionAnswer.questionId === this.activeQuestion.id &&
                kb.questionAnswer.selectedAnswers.some(a => selectedArray.includes(a))
            );
        },
        activeContentBlocks() {
            return [
                ...this.activeKnowledgeBlocks,
                ...this.activeExplanations,
            ];
        },
        questionXOfY() {
            return this.dataQuestionXofYLabel.replace(constants.replaces.current, this.page).replace(constants.replaces.total, this.questionnaire?.questions?.length);
        },
        selectedXOfY() {
            if (!this.activeQuestion || this.activeQuestion.maxSelectAmount === 1) return '';
            const answer = this.answers[this.activeQuestion.id];
            if (!Array.isArray(answer)) return '';
            return this.dataSelectedXofYLabel.replace(constants.replaces.selected, answer.length).replace(constants.replaces.total, this.activeQuestion.maxSelectAmount);
        },
        buttonDisabled() {
            if (!this.activeQuestion) return false;
            const answer = this.answers[this.activeQuestion.id];
            if (!answer) return true;
            if (Array.isArray(answer)) {
                return answer.length < this.activeQuestion.minSelectAmount || answer.length > this.activeQuestion.maxSelectAmount;
            }
            return false;
        },
        isAtFinish() {
            return this.page === this.questionnaire?.questions?.length && !(this.activeExplanations.length && this.isProgressing);
        },
    },
    methods: {
        async fetchData() {
            try {
                this.loading = true;
                this.questionnaire = await readQuestionnaire({ id: this.dataGuid, cultureCode: this.dataCulture });
                this.questionnaire?.questions.forEach(q => {
                    if (!(q.minSelectAmount === 1 && q.maxSelectAmount === 1) && q.type !== constants.api.question.types.horizontalScale) {
                        this.answers[q.id] = [];
                    } else {
                        this.answers[q.id] = null;
                    }
                });
            } catch (error) {
                console.error('Error fetching questionnaire data:', error);
            } finally {
                this.loading = false;
            }
        },
        async openModal() {
            if (!this.response) this.response = await createResponse({ questionnaireId: this.questionnaire.id, cultureCode: this.dataCulture });
            this.showModal = true;
        },
        async closeModalPrompt() {
            const swal = await this.$swal.fire({
                title: this.dataStopQuestionnaireTitle,
                icon: constants.icons.question,
                text: this.dataStopQuestionnaireText,
                confirmButtonText: this.dataStopQuestionnaireConfirm,
                cancelButtonText: this.dataStopQuestionnaireCancel,
                didOpen: () => {
                    const confirmBtn = document.querySelector('.swal2-confirm');
                    if (confirmBtn) {
                        confirmBtn.id = 'questionnaire-close';
                    }
                }
            });
            if (swal.isConfirmed) {
                this.closeModal();

                if (this.dataReferAfterCancel) {
                    if (this.dataReferralPage) {
                        window.location.href = this.dataReferralPage;
                    } else {
                        window.history.back();
                    }
                }
            }
        },
        closeModal() {
            this.page = 0;
            this.showModal = false;
            this.response = null;
        },
        prevPage() {
            if (this.activeContentBlocks.length) {
                if (this.isProgressing) {
                    this.page--;
                } else {
                    this.isProgressing = true;
                }
            } else {
                if (this.page) this.page--;
            }
        },
        nextPage() {
            if (this.activeContentBlocks.length && this.isProgressing) {
                this.isProgressing = false;
            } else {
                this.isProgressing = true;
                if (this.page < this.questionnaire?.questions?.length + 1) this.page++;
            }
        },
        async finish() {
            await finishResponse({ questionnaireId: this.questionnaire.id, responseId: this.response.id });
            this.fullResponse = await readResponse({ questionnaireId: this.questionnaire.id, responseId: this.response.id });

            const $widgetGridsQuestionAnswer = document.querySelectorAll('.widget-grid--question-answer');
            $widgetGridsQuestionAnswer.forEach($grid => this.initializeGridQuestionAnswer($grid));

            const $widgetGridsScale = Array.from(document.querySelectorAll('.widget-grid--scale'));
            $widgetGridsScale.forEach($grid => this.initializeGridScale($grid));

            const $container = document.getElementById('questionnaire-results');
            $container.classList.remove('is-hidden');

            if (this.questionnaire.scales && this.questionnaire.scales.length > 1) {
                $widgetGridsScale.sort(($gridA, $gridB) => {
                    const showDataA = JSON.parse($gridA.dataset.showJson);
                    const showDataB = JSON.parse($gridB.dataset.showJson);
                    const scoreA = this.fullResponse.scaleScores[showDataA.id] || 0;
                    const scoreB = this.fullResponse.scaleScores[showDataB.id] || 0;
                    return scoreB - scoreA;
                });

                $widgetGridsScale.forEach($grid => $container.appendChild($grid));
            }

            this.closeModal();

            this.$nextTick(() => {
                window.dispatchEvent(new CustomEvent('questionnaire:results'));
            });
        },
        async onInput() {
            let optionValues = this.answers[this.activeQuestion.id];
            if (!Array.isArray(optionValues)) optionValues = [optionValues];

            await updateResponse({
                questionnaireId: this.questionnaire.id,
                responseId: this.response.id,
                questionId: this.activeQuestion.id,
                optionValues: optionValues.map(v => { return { AnswerOptionId: v } }),
            });
        },
        isVideo(type) {
            return type === constants.api.question.mediaTypes.youtube || type === constants.api.question.mediaTypes.vimeo;
        },
        getKey(block) {
            if (!block) return null;
            const id = block.questionId || block.questionAnswer?.questionId;
            return `${id}-${block.title}`;
        },
        getMediaValue(block) {
            if (!block) return null;
            return block.youtubeId || block.vimeoId || block.images?.length || null;
        },
        getMediaType(block) {
            if (!block) return null;
            if (block.youtubeId) return constants.api.question.mediaTypes.youtube;
            if (block.vimeoId) return constants.api.question.mediaTypes.vimeo;
            if (block.images?.length) return constants.api.question.mediaTypes.image;
            return null;
        },
        initializeGridQuestionAnswer($grid) {
            if (!this.fullResponse || !$grid.dataset.showJson) return;
            const showData = JSON.parse($grid.dataset.showJson);
            const answerArray = this.fullResponse.answers.find(a => a.questionnaireQuestionId === showData.questionId).answerOptionSelections;
            const selectedArray = answerArray.map(a => a.questionnaireQuestionAnswerOptionId);
            const shouldShow = selectedArray.some(a => showData.selectedAnswers.includes(a));
            if (shouldShow) $grid.classList.remove('is-hidden');
        },
        initializeGridScale($grid) {
            if (!this.fullResponse || !$grid.dataset.showJson) return;
            const showData = JSON.parse($grid.dataset.showJson);
            const scalescore = this.fullResponse.scaleScores[showData.id];
            const shouldShow = scalescore >= showData.min && scalescore <= showData.max;
            if (shouldShow) $grid.classList.remove('is-hidden');
            const $scoreElements = $grid.querySelectorAll('.has-questionnaire-score');
            $scoreElements.forEach($el => {
                $el.innerHTML = $el.innerHTML.replace(/{score}/g, scalescore);
            });
        },
    },
    async mounted() {
        this.dataGuid = this.$el.dataset.guid;
        if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
        if (this.$el.dataset.theme) this.dataTheme = this.$el.dataset.theme;
        if (this.$el.dataset.hideCookie) this.dataHideCookie = this.$el.dataset.hideCookie === 'True';
        if (this.$el.dataset.questionXofyLabel) this.dataQuestionXofYLabel = this.$el.dataset.questionXofyLabel;
        if (this.$el.dataset.selectedXofyLabel) this.dataSelectedXofYLabel = this.$el.dataset.selectedXofyLabel;
        if (this.$el.dataset.stopQuestionnaireTitle) this.dataStopQuestionnaireTitle = this.$el.dataset.stopQuestionnaireTitle;
        if (this.$el.dataset.stopQuestionnaireText) this.dataStopQuestionnaireText = this.$el.dataset.stopQuestionnaireText;
        if (this.$el.dataset.stopQuestionnaireConfirm) this.dataStopQuestionnaireConfirm = this.$el.dataset.stopQuestionnaireConfirm;
        if (this.$el.dataset.stopQuestionnaireCancel) this.dataStopQuestionnaireCancel = this.$el.dataset.stopQuestionnaireCancel;
        if (this.$el.dataset.explanationsJson) this.dataExplanationsJson = JSON.parse(this.$el.dataset.explanationsJson);
        if (this.$el.dataset.knowledgeBlocksJson) this.dataKnowledgeBlocksJson = JSON.parse(this.$el.dataset.knowledgeBlocksJson);
        if (this.$el.dataset.referAfterCancel) this.dataReferAfterCancel = this.$el.dataset.referAfterCancel === 'True';
        if (this.$el.dataset.referralPage) this.dataReferralPage = this.$el.dataset.referralPage;

        if (this.dataTheme) {
            const body = document.body;
            body.classList.forEach(styleClass => {
                if (styleClass.startsWith('theme-')) document.body.classList.remove(styleClass);
            });

            body.classList.add(this.dataTheme);
        }

        const params = new URLSearchParams(window.location.search);
        this.isIframe = params.has('iframe');

        if (this.isIframe) {
            document.body.classList.add('is-iframe');

            const observerTarget = document.documentElement;
            observerTarget.classList.add('is-clipped');
            const resizeObserver = new ResizeObserver(() => {
                let height = observerTarget.offsetHeight;
                window.parent.postMessage({ type: 'questionnaire:results', height }, '*');
            });

            resizeObserver.observe(observerTarget);
        } else if (this.dataHideCookie) {
            const $cookie = document.querySelector('.cookie');
            if ($cookie) $cookie.classList.add('is-hidden');
        }

        await this.fetchData();

        await this.openModal();

        const $container = document.getElementById('questionnaire-results');
        $container.classList.add('is-hidden');
    },
};

export default questionnaireEmbedApp;
